import { useMemo, useState } from "react";
import { type LayoutChangeEvent, View } from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";

// Bar metrics mirror the playback waveform in MessageBubble (WaveformVisualizer
// props) so recording and playback waves look identical.
const BAR_WIDTH = 6;
const BAR_GAP = 2;
const MIN_HEIGHT = 3;
const MAX_HEIGHT = 18;

/**
 * Live recording waveform (ChatGPT-style). Renders the most recent audio meter
 * levels as right-aligned bars; newest sample on the right, silence padded on
 * the left so the wave scrolls in as you speak.
 */
export const RecordingWave = ({ levels }: { levels: number[] }) => {
	const theme = useAppTheme();
	const [width, setWidth] = useState(0);

	const barCount = Math.max(
		1,
		Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP)),
	);
	// Stable keys so the sliding window of levels doesn't use array indices.
	const slots = useMemo(
		() => Array.from({ length: barCount }, (_, i) => ({ id: `wave-${i}` })),
		[barCount],
	);

	const recent = levels.slice(-barCount);
	const offset = barCount - recent.length;

	const onLayout = (event: LayoutChangeEvent) => {
		const next = event.nativeEvent?.layout?.width ?? 0;
		if (next > 0) {
			setWidth(next);
		}
	};

	return (
		<View
			onLayout={onLayout}
			style={{
				flex: 1,
				flexDirection: "row",
				alignItems: "center",
				gap: BAR_GAP,
				height: 28,
				overflow: "hidden",
			}}
		>
			{width > 0
				? slots.map((slot, i) => {
						const level = i >= offset ? recent[i - offset] : 0;
						return (
							<View
								key={slot.id}
								style={{
									width: BAR_WIDTH,
									height: MIN_HEIGHT + level * (MAX_HEIGHT - MIN_HEIGHT),
									borderRadius: BAR_WIDTH / 2,
									backgroundColor: theme.colors.tertiary,
									opacity: 0.3 + level * 0.7,
								}}
							/>
						);
					})
				: null}
		</View>
	);
};
